import React, { useEffect, useMemo, useState } from 'react';

import CustomModal from 'src/components/customModal/CustomModal';
import { REACT_APP_CLOUD_FRONT } from 'src/constants';
import {
  IItemsRequiredModifierGroups,
  IRequiredModifiersGroupsModifiers,
  ISubCategories,
  ItemDetails,
} from 'src/models/item.model';
import { IBerryRecommendedItem } from 'src/models/berry.model';
import {
  BerryItemFamily,
  BerrySize,
  IBerryAddOnChoice,
  IBerryItemSelection,
  IBerryPackageCategoryChoice,
  IBerryPackageOptionalChoice,
  IBerryRequiredChoice,
  IBerrySubItemChoice,
  IBerrySubItemGroup,
  canIncrementSubItem,
  defaultSubItemQuantity,
  deltaLabel,
  getBerryItemFamily,
  getSizeTotalTarget,
  hasSizeToggle,
  isManualPackageCategory,
  isUnavailableAt,
  pickDelta,
  requiredChoiceDelta,
  resolveSizeBrinkItemId,
  resolveSizePrice,
  resolveSubItemModifierSize,
  selectionLineTotal,
  sizePlanLabel,
  subItemDeltaPerIncrement,
  suggestedTraySize,
  sumGroupQuantities,
} from '../helper/berryItemConfig.helper';
import useBerryMenuItemDetail from '../hooks/useBerryMenuItemDetail';
import useSiteItemCustomization from '../hooks/useSiteItemCustomization';
import SiteItemCustomizer from './SiteItemCustomizer';
import styles from '../berryChat.module.scss';

/** Fields real on the wire (confirmed via src/containers/OtherManuItems and
 * src/helper/sandwichTrayChips.helper.ts) but not on the shared ItemDetails type. */
type FetchedItem = ItemDetails & {
  is_sandwich_tray?: number | boolean;
  is_soup_tray?: number | boolean;
  is_salad_tray?: number | boolean;
  sub_items_required_modifier_groups?: IBerrySubItemGroup[];
};

/** A Soup Tray's optional "Soup Mix-Ins" group (`items_modifier_groups`). */
interface IBerryAddOnGroup {
  id: number;
  name: string;
  label?: string;
  max?: number | null;
  base?: number | null;
  extendable_limit?: number | null;
  modifiers_groups_modifiers: Array<{
    id: number;
    name: string;
    price?: number | null;
    max?: number | null;
    image?: string;
    calories?: number | null;
    brink_modifier_id: number;
    modifier_location?: any[];
  }>;
}

type Step =
  | { kind: 'size' }
  | { kind: 'required'; group: IItemsRequiredModifierGroups; required: boolean }
  // `impliedSize` is set for a nested tray inside a package (see
  // resolvedPackageCategoryItem below) — packages fix the nested tray at
  // FULL size themselves (no size step, no user toggle), so the sub-item
  // counter step needs a size to compute its target/defaults against even
  // though the component-level `size` state stays null for packages.
  | { kind: 'subitems'; group: IBerrySubItemGroup; impliedSize?: BerrySize }
  | { kind: 'package_category'; category: ISubCategories; manual: boolean }
  | { kind: 'addons'; group: IBerryAddOnGroup }
  // The item page's own Customize section, driven by the site's engine
  // (salad trays — B17/NEW-4; see hooks/useSiteItemCustomization).
  | { kind: 'site_customize' }
  | { kind: 'review' };

/** The item that actually fills a package category slot: the manually chosen
 * candidate, or the sole item for an auto-included (single-item) category.
 * Can itself be a full tray (e.g. item 319 nested inside item 324's
 * "Sandwich Trays" category) — hence the same untyped-but-real extension
 * as FetchedItem. */
function resolvedPackageCategoryItem(
  category: ISubCategories,
  packageChoices: Record<number, ItemDetails | null>,
): FetchedItem | null {
  if (isManualPackageCategory(category)) {
    return (packageChoices[category.id] as FetchedItem | undefined) ?? null;
  }
  return (category.items?.[0] as FetchedItem | undefined) ?? null;
}

interface Props {
  show: boolean;
  item: IBerryRecommendedItem;
  locationId: number | string | null | undefined;
  authUser?: { id?: number; type?: string | null };
  /** Which entry of item.size_plan this opening configures (0 = the first). */
  planStep?: number;
  onCancel: () => void;
  onConfirm: (selection: IBerryItemSelection) => Promise<void>;
}

const priceLabel = (value: number | undefined | null): string =>
  `$${(value ?? 0).toFixed(2)}`;

/** The group's per-size base price for one counter step (sandwich/soup) —
 * `size_one_min_mod_price` for a Small tray, `size_two_` for Large. */
function subItemGroupPricing(group: IBerrySubItemGroup, size: BerrySize): { min_mod_price?: number | null } {
  const g = group as IBerrySubItemGroup & {
    min_mod_price?: number | null;
    size_one_min_mod_price?: number | null;
    size_two_min_mod_price?: number | null;
  };
  const bySize = size === 'HALF' ? g.size_one_min_mod_price : g.size_two_min_mod_price;
  return { min_mod_price: bySize ?? g.min_mod_price ?? null };
}

const ItemConfigPicker: React.FC<Props> = ({
  show,
  item,
  locationId,
  authUser,
  planStep = 0,
  onCancel,
  onConfirm,
}) => {
  const { data: itemDetails, isLoading, error } = useBerryMenuItemDetail({
    location_id: locationId,
    category_id: item.category_id,
    item_id: item.item_id,
    user: authUser,
    enabled: show,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [size, setSize] = useState<BerrySize | null>(null);
  const [requiredChoices, setRequiredChoices] = useState<
    Record<number, IBerryRequiredChoice | null>
  >({});
  const [subItemQuantities, setSubItemQuantities] = useState<
    Record<number, Record<number, number>>
  >({});
  const [packageChoices, setPackageChoices] = useState<
    Record<number, ItemDetails | null>
  >({});
  // How many of the whole configured item (tray/package) to add — chosen on
  // the Review step, mirrors the item-detail page's own +/- quantity control.
  const [quantity, setQuantity] = useState(1);
  // Soup mix-ins: group id → modifier id → servings.
  const [addOnQuantities, setAddOnQuantities] = useState<Record<number, Record<number, number>>>({});
  // Berry's suggested size (UAT row 12) is applied once per opening, after the
  // item's details load; what it was set to, for the note above the steps.
  const [sizePrefillDone, setSizePrefillDone] = useState(false);
  const [prefilledSize, setPrefilledSize] = useState<BerrySize | null>(null);

  // Reset all picker state whenever a new item (or the next size of Berry's
  // plan for the same item) is opened.
  useEffect(() => {
    setStepIndex(0);
    setSize(null);
    setSizePrefillDone(false);
    setPrefilledSize(null);
    setRequiredChoices({});
    setSubItemQuantities({});
    setPackageChoices({});
    setAddOnQuantities({});
    setQuantity(item.suggested_quantity && item.suggested_quantity > 0 ? item.suggested_quantity : 1);
    setSubmitError(null);
    setIsSubmitting(false);
  }, [show, item.item_id, planStep]);

  const family: BerryItemFamily = useMemo(
    () => getBerryItemFamily(itemDetails ?? item),
    [itemDetails, item],
  );

  const fetched = itemDetails as FetchedItem | null;

  const subItemGroups: IBerrySubItemGroup[] = useMemo(
    () => (fetched?.sub_items_required_modifier_groups ?? []) as IBerrySubItemGroup[],
    [fetched],
  );

  const requiredGroups: IItemsRequiredModifierGroups[] = useMemo(
    () => fetched?.items_required_modifier_groups ?? [],
    [fetched],
  );

  const packageCategories: ISubCategories[] = useMemo(
    () => fetched?.package_categories ?? [],
    [fetched],
  );

  // Box lunches list their pick-one groups (a sandwich list, a soup list) as
  // `sub_categories` rather than `package_categories`.
  // Optional per-serving add-ons the site offers on a Soup Tray ("Soup
  // Mix-Ins"). Salad-tray customizations (also items_modifier_groups) are a
  // separate, not-yet-built feature (B17), so only soup trays get this step.
  const addOnGroups: IBerryAddOnGroup[] = useMemo(
    () =>
      fetched?.is_soup_tray
        ? (((fetched as any).items_modifier_groups ?? []) as IBerryAddOnGroup[]).filter(
            (g) => (g.modifiers_groups_modifiers ?? []).length > 0,
          )
        : [],
    [fetched],
  );

  // Salad trays are customized with the site's own engine and Customize
  // section, so every production rule (included ingredients, swaps, limits,
  // priced extras) applies unchanged. Create-your-own trays use a different
  // site flow (first-time modal) and stay on the plain path.
  const usesSiteCustomization = Boolean(
    fetched?.is_salad_tray &&
      !fetched?.is_package &&
      // same test as helperMethods.isItCreateYourOwnItem
      !(fetched as any)?.is_create_your_own,
  );
  const siteCustomization = useSiteItemCustomization(
    usesSiteCustomization ? fetched : null,
    show && usesSiteCustomization,
  );

  const subCategories: ISubCategories[] = useMemo(
    () => (fetched?.sub_categories ?? []) as ISubCategories[],
    [fetched],
  );

  // Packages never get a size toggle, even defensively — they have no
  // half/full split by design (see spec: "Packages skip straight to their
  // sub-selections since they have no size toggle").
  const showSizeStep = useMemo(
    () => (fetched && family !== 'package' ? hasSizeToggle(fetched) : false),
    [fetched, family],
  );

  // Open at the size Berry recommended for the group (UAT row 12): the size
  // step is answered and skipped; Back still reaches it to change the size.
  useEffect(() => {
    if (sizePrefillDone || !fetched || !show) return;
    setSizePrefillDone(true);
    if (!showSizeStep || size != null) return;
    const suggested = suggestedTraySize(item, fetched, family);
    if (!suggested) return;
    setSize(suggested);
    setPrefilledSize(suggested);
    setStepIndex((i) => (i === 0 ? 1 : i));
  }, [sizePrefillDone, fetched, show, showSizeStep, size, item, family]);

  // "I'll Choose" (HALF) → the customer picks each sub-category item;
  // "Choose For Me" (FULL) → the kitchen fills, so those steps are skipped.
  // Before a size is chosen the user is still parked on the size step, so this
  // value doesn't gate forward progress; default to showing the picks when a
  // box lunch somehow has no size toggle at all.
  const boxLunchPicksShown = useMemo(
    () => family === 'box_lunch' && (!showSizeStep || size === 'HALF'),
    [family, showSizeStep, size],
  );

  // Auto-include single-item package categories immediately so they read as
  // valid without a user action, and so the review step has something to show.
  useEffect(() => {
    if (family !== 'package' || packageCategories.length === 0) return;
    setPackageChoices((prev) => {
      const next = { ...prev };
      let changed = false;
      packageCategories.forEach((cat) => {
        if (!isManualPackageCategory(cat) && next[cat.id] === undefined) {
          next[cat.id] = cat.items?.[0] ?? null;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [family, packageCategories]);

  // Recompute sub-item defaults whenever the size changes (targets differ: 12/24, 7/15).
  useEffect(() => {
    if (!size || subItemGroups.length === 0) return;
    setSubItemQuantities((prev) => {
      const next = { ...prev };
      subItemGroups.forEach((group) => {
        const defaults: Record<number, number> = {};
        group.items_as_modifier.forEach((option) => {
          defaults[option.id] = defaultSubItemQuantity(option, group, size);
        });
        next[group.id] = defaults;
      });
      return next;
    });
  }, [size, subItemGroups]);

  // Sub-item groups nested inside a package category's tray (e.g. item 319's
  // bread/chips/sandwich-count nested inside item 324's "Sandwich Trays"
  // category). Fixed at FULL size by the package itself — no toggle, so
  // (unlike the effect above) this initializes once per group rather than
  // resetting on a `size` state that never changes for packages.
  const nestedSubitemGroups: IBerrySubItemGroup[] = useMemo(() => {
    if (family !== 'package') return [];
    const result: IBerrySubItemGroup[] = [];
    packageCategories.forEach((category) => {
      const resolved = resolvedPackageCategoryItem(category, packageChoices);
      (resolved?.sub_items_required_modifier_groups ?? []).forEach((group) =>
        result.push(group),
      );
    });
    return result;
  }, [family, packageCategories, packageChoices]);

  useEffect(() => {
    if (nestedSubitemGroups.length === 0) return;
    setSubItemQuantities((prev) => {
      const next = { ...prev };
      let changed = false;
      nestedSubitemGroups.forEach((group) => {
        if (next[group.id]) return; // init once — implied size is fixed, no reset needed
        const defaults: Record<number, number> = {};
        group.items_as_modifier.forEach((option) => {
          defaults[option.id] = defaultSubItemQuantity(option, group, 'FULL');
        });
        next[group.id] = defaults;
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [nestedSubitemGroups]);

  const steps: Step[] = useMemo(() => {
    if (!fetched) return [];
    const result: Step[] = [];
    if (showSizeStep) result.push({ kind: 'size' });

    if (family === 'package') {
      packageCategories.forEach((category) => {
        result.push({
          kind: 'package_category',
          category,
          manual: isManualPackageCategory(category),
        });
        // A package category's item (manually chosen or auto-included) can
        // itself be a full tray with its own bread/chips/sub-item groups —
        // confirmed via item 324's "Sandwich Trays" category nesting item
        // 319 whole, groups included. Recurse into it right after its
        // category step, same ordering as the reference item-detail page.
        const resolved = resolvedPackageCategoryItem(category, packageChoices);
        if (!resolved) return;
        (resolved.items_required_modifier_groups ?? []).forEach((group) =>
          result.push({ kind: 'required', group, required: (group.min ?? 1) > 0 }),
        );
        (resolved.sub_items_required_modifier_groups ?? []).forEach((group) =>
          result.push({ kind: 'subitems', group, impliedSize: 'FULL' }),
        );
      });
      requiredGroups.forEach((group) =>
        result.push({ kind: 'required', group, required: (group.min ?? 0) > 0 }),
      );
    } else if (family === 'box_lunch') {
      // Only the "I'll Choose" (HALF) variant surfaces the sandwich/soup
      // pickers; "Choose For Me" (FULL) skips straight to dessert/drink. Each
      // picked sandwich brings its own required Bread group (a soup brings
      // none), nested right after its pick — same recursion as packages.
      if (boxLunchPicksShown) {
        subCategories.forEach((category) => {
          result.push({ kind: 'package_category', category, manual: true });
          const chosen = packageChoices[category.id] as FetchedItem | null;
          (chosen?.items_required_modifier_groups ?? []).forEach((group) =>
            result.push({ kind: 'required', group, required: (group.min ?? 1) > 0 }),
          );
        });
      }
      // Dessert (group 162) and Drink (group 170) — both required top-level
      // single-selects ("No Drink" is an explicit option, so drink is always
      // satisfiable).
      requiredGroups.forEach((group) =>
        result.push({ kind: 'required', group, required: (group.min ?? 1) > 0 }),
      );
    } else if (usesSiteCustomization) {
      // The site's Customize section covers every group (required ones too).
      result.push({ kind: 'site_customize' });
    } else {
      requiredGroups.forEach((group) =>
        result.push({ kind: 'required', group, required: (group.min ?? 1) > 0 }),
      );
      subItemGroups.forEach((group) => result.push({ kind: 'subitems', group }));
      addOnGroups.forEach((group) => result.push({ kind: 'addons', group }));
    }

    result.push({ kind: 'review' });
    return result;
  }, [
    fetched,
    family,
    showSizeStep,
    boxLunchPicksShown,
    subCategories,
    packageCategories,
    packageChoices,
    requiredGroups,
    subItemGroups,
    addOnGroups,
    usesSiteCustomization,
  ]);

  const currentStep = steps[stepIndex];

  const isStepValid = (step: Step | undefined): boolean => {
    if (!step) return false;
    switch (step.kind) {
      case 'size':
        return size != null;
      case 'required':
        return !step.required || requiredChoices[step.group.id] != null;
      case 'subitems':
        return true; // min:0 — never blocks progress
      case 'addons':
        return true; // optional
      case 'site_customize':
        return siteCustomization != null && siteCustomization.missingRequired == null;
      case 'package_category':
        return !step.manual || packageChoices[step.category.id] != null;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const canProceed = isStepValid(currentStep);
  const isLastStep = stepIndex === steps.length - 1;

  const goNext = () => {
    if (!canProceed) return;
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  // Builds the pick list for one sub-item group: any option that's currently
  // picked (qty > 0) OR was ever a default (defaultQuantity > 0) — a default
  // the user zeroed out is still included, at quantity 0, never silently
  // dropped (buildCartItemFromSelection derives "code" from comparing
  // quantity against defaultQuantity — see subItemModifier). `modifierSize`
  // is resolved against `parentTrayItem` (the tray this group belongs to),
  // not `traySize` — see resolveSubItemModifierSize. `traySize` (the actual
  // HALF/FULL tray configuration — fixed "FULL" for a nested package tray,
  // user-chosen for a standalone one) is only used to look up each option's
  // default quantity at that size.
  const buildSubItemChoicesForGroup = (
    group: IBerrySubItemGroup,
    parentTrayItem: FetchedItem,
    traySize: BerrySize,
  ): IBerrySubItemChoice[] => {
    const modifierSize = resolveSubItemModifierSize(parentTrayItem);
    const quantities = subItemQuantities[group.id] ?? {};
    const increment = group.item_counter_increment || 1;
    const groupPricing = subItemGroupPricing(group, traySize);
    const result: IBerrySubItemChoice[] = [];
    group.items_as_modifier.forEach((option) => {
      const qty = quantities[option.id] ?? 0;
      const defaultQuantity = defaultSubItemQuantity(option, group, traySize);
      if (qty <= 0 && defaultQuantity <= 0) return;
      result.push({
        groupId: group.id,
        itemId: option.id,
        name: option.name,
        quantity: qty,
        unitPrice: option.price ?? 0,
        modifierSize,
        defaultQuantity,
        increment,
        deltaPerIncrement: subItemDeltaPerIncrement(option, groupPricing),
      });
    });
    return result;
  };

  const buildSelection = (): IBerryItemSelection | null => {
    if (!fetched) return null;
    if (usesSiteCustomization) {
      if (!siteCustomization) return null;
      return {
        family,
        itemId: Number(item.item_id),
        categoryId: String(fetched.category_id ?? item.category_id ?? ''),
        name: fetched.name,
        size: null,
        brinkItemId: fetched.full_brink_item_id ?? Number(fetched.id),
        // Tray price with the customization, exactly as the item page shows it.
        price: siteCustomization.unitPrice,
        quantity,
        requiredChoices: [],
        subItems: [],
        packageCategories: [],
        packageOptional: [],
        addOns: [],
        siteCartItem: siteCustomization.buildCartItem(quantity),
        siteChanges: siteCustomization.changes,
      };
    }
    const price = size ? resolveSizePrice(fetched, size) : fetched.price;
    const brinkItemId = size
      ? resolveSizeBrinkItemId(fetched, size)
      : fetched.full_brink_item_id ?? Number(fetched.id);

    // Package's own top-level groups (e.g. the Dessert/Drink upsell) go into
    // packageOptional below. Nested tray picks (bread/chips/sandwich) live on
    // their own package-category entry, not here — see packageCategoriesArr.
    // Packages and box lunches both keep the top-level `requiredChoices`/
    // `subItems` empty: their picks live on package/sub-category entries
    // (nested) or in `packageOptional` (dessert/drink) instead.
    const usesCategoryModel = family === 'package' || family === 'box_lunch';

    const requiredChoicesArr: IBerryRequiredChoice[] = usesCategoryModel
      ? []
      : (Object.values(requiredChoices).filter(Boolean) as IBerryRequiredChoice[]);

    const subItemsArr: IBerrySubItemChoice[] = usesCategoryModel
      ? []
      : subItemGroups.flatMap((group) =>
          buildSubItemChoicesForGroup(group, fetched, size ?? 'FULL'),
        );

    const packageCategoriesArr: IBerryPackageCategoryChoice[] =
      family === 'package'
        ? packageCategories.map((cat) => {
            const resolved = resolvedPackageCategoryItem(cat, packageChoices);
            const nestedRequiredGroups = resolved?.items_required_modifier_groups ?? [];
            const nestedSubGroups = resolved?.sub_items_required_modifier_groups ?? [];
            const hasNested = nestedRequiredGroups.length > 0 || nestedSubGroups.length > 0;
            return {
              categoryId: cat.id,
              categoryName: cat.name,
              manual: isManualPackageCategory(cat),
              chosenItem: resolved ?? null,
              delta: pickDelta(resolved as any),
              nestedItemId: hasNested && resolved ? Number(resolved.id) : undefined,
              nestedRequiredChoices: hasNested
                ? (nestedRequiredGroups
                    .map((group) => requiredChoices[group.id])
                    .filter(Boolean) as IBerryRequiredChoice[])
                : undefined,
              nestedSubItems:
                hasNested && resolved
                  ? nestedSubGroups.flatMap((group) =>
                      buildSubItemChoicesForGroup(group, resolved, 'FULL'),
                    )
                  : undefined,
            };
          })
        : family === 'box_lunch' && boxLunchPicksShown
        ? // "I'll Choose": each sub-category pick, with its own required Bread
          // group (sandwich) nested. "Choose For Me" omits these entirely.
          subCategories.map((cat) => {
            const chosen = (packageChoices[cat.id] as FetchedItem | null) ?? null;
            const nestedRequiredGroups = chosen?.items_required_modifier_groups ?? [];
            const hasNested = nestedRequiredGroups.length > 0;
            return {
              categoryId: cat.id,
              categoryName: cat.name,
              manual: true,
              chosenItem: chosen,
              delta: pickDelta(chosen as any),
              nestedItemId: hasNested && chosen ? Number(chosen.id) : undefined,
              nestedRequiredChoices: hasNested
                ? (nestedRequiredGroups
                    .map((group) => requiredChoices[group.id])
                    .filter(Boolean) as IBerryRequiredChoice[])
                : undefined,
              nestedSubItems: undefined,
            };
          })
        : [];

    // Dessert + Drink (box lunch) / the Dessert upsell (package) — both are
    // the item's own top-level required groups.
    const packageOptionalArr: IBerryPackageOptionalChoice[] = usesCategoryModel
      ? requiredGroups.map((group) => ({
          groupId: group.id,
          choice: requiredChoices[group.id] ?? null,
        }))
      : [];

    const addOnsArr: IBerryAddOnChoice[] = addOnGroups.flatMap((group) =>
      group.modifiers_groups_modifiers
        .filter((m) => (addOnQuantities[group.id]?.[m.id] ?? 0) > 0)
        .map((m) => ({
          groupId: group.id,
          modifierId: m.id,
          brinkModifierId: m.brink_modifier_id,
          name: m.name,
          price: m.price ?? 0,
          quantity: addOnQuantities[group.id][m.id],
          max: m.max ?? 0,
          image: m.image ? `${REACT_APP_CLOUD_FRONT}${m.image}` : undefined,
          calories: m.calories ?? null,
          groupMax: group.max ?? 0,
          groupBase: group.base ?? 0,
          extendableLimit: group.extendable_limit ?? 99,
        })),
    );

    return {
      family,
      itemId: Number(item.item_id),
      categoryId: String(fetched.category_id ?? item.category_id ?? ''),
      name: fetched.name,
      size,
      brinkItemId,
      price,
      quantity,
      requiredChoices: requiredChoicesArr,
      subItems: subItemsArr,
      packageCategories: packageCategoriesArr,
      packageOptional: packageOptionalArr,
      addOns: addOnsArr,
    };
  };

  const handleConfirm = async () => {
    const selection = buildSelection();
    if (!selection) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm(selection);
    } catch {
      setSubmitError('Failed to add. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectRequired = (
    group: IItemsRequiredModifierGroups,
    modifier: IRequiredModifiersGroupsModifiers | null,
  ) => {
    setRequiredChoices((prev) => ({
      ...prev,
      [group.id]: modifier
        ? {
            groupId: group.id,
            brinkModifierGroupId: group.brink_modifier_group_id,
            modifierId: modifier.id,
            brinkModifierId: modifier.brink_modifier_id,
            name: modifier.name,
            price: modifier.price ?? 0,
            image: modifier.image,
            calories: modifier.calories,
            inItem: Number(modifier.in_item ?? 0),
            groupMax: group.max ?? 1,
            groupBase: group.base ?? 0,
            extendableLimit: group.extendable_limit ?? 1,
            delta: requiredChoiceDelta(modifier, group),
          }
        : null,
    }));
  };

  const adjustSubItemQty = (group: IBerrySubItemGroup, optionId: number, delta: number) => {
    setSubItemQuantities((prev) => {
      const current = prev[group.id]?.[optionId] ?? 0;
      const next = Math.max(0, current + delta);
      return { ...prev, [group.id]: { ...prev[group.id], [optionId]: next } };
    });
  };

  // Box lunches label the toggle "I'll Choose" (HALF) / "Choose For Me" (FULL)
  // — a build mode, not a portion — falling back to the item's own labels.
  const sizeOptionLabel = (s: BerrySize): string => {
    if (family === 'box_lunch') {
      return s === 'HALF'
        ? fetched?.half_item_label || "I'll Choose"
        : fetched?.full_item_label || 'Choose For Me';
    }
    return s === 'HALF' ? 'Small' : 'Large';
  };

  const renderSizeStep = () => (
    <div className={styles.configStep}>
      <p className={styles.configStepLabel}>
        {family === 'box_lunch' ? 'How would you like to build it?' : 'Choose a size'}
      </p>
      <div className={styles.configSizeToggle}>
        {(['HALF', 'FULL'] as BerrySize[]).map((s) => (
          <button
            key={s}
            type="button"
            className={`${styles.configSizeBtn} ${size === s ? styles.configSizeBtnActive : ''}`}
            onClick={() => setSize(s)}
          >
            {sizeOptionLabel(s)}
            {family !== 'box_lunch' && (
              <span className={styles.configSizeBtnPrice}>
                {priceLabel(s === 'HALF' ? fetched?.half_brink_item_price : fetched?.price)}
              </span>
            )}
            {prefilledSize === s && (
              <span className={styles.configSizeSuggested}>Suggested</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // "Set to Large for your group." / for a two-size plan "Plan for your group:
  // 1 Large + 1 Small. This is the Large (1 of 2)." The size is Berry's plan or
  // what the customer asked for, so the note doesn't say whose.
  const renderSizePlanNote = () => {
    if (!prefilledSize || size !== prefilledSize || currentStep?.kind === 'size') return null;
    const plan = item.size_plan ?? [];
    const sizeName = prefilledSize === 'HALF' ? 'Small' : 'Large';
    const text =
      plan.length > 1
        ? `Plan for your group: ${sizePlanLabel(plan)}. This is the ${sizeName} (${
            planStep + 1
          } of ${plan.length}) — tap Back to change the size.`
        : `Set to ${sizeName} for your group — tap Back to change the size.`;
    return (
      <p className={styles.configPlanNote} data-testid="berry-size-plan-note">
        {text}
      </p>
    );
  };

  const renderRequiredStep = (step: Extract<Step, { kind: 'required' }>) => {
    const { group, required } = step;
    const selected = requiredChoices[group.id];
    return (
      <div className={styles.configStep}>
        <p className={styles.configStepLabel}>
          {group.label || group.name}
          {!required && <span className={styles.configOptionalTag}> (optional)</span>}
        </p>
        <div className={styles.configOptionList}>
          {group.required_modifiers_groups_modifiers.map((mod) => {
            const unavailable = isUnavailableAt(mod as any, locationId);
            const label = deltaLabel(requiredChoiceDelta(mod, group));
            return (
              <button
                key={mod.id}
                type="button"
                disabled={unavailable}
                className={`${styles.configOptionCard} ${
                  selected?.modifierId === mod.id ? styles.configOptionCardActive : ''
                }`}
                onClick={() => selectRequired(group, mod)}
              >
                <span>{mod.name}</span>
                {unavailable ? (
                  <span className={styles.configOptionalTag}>Unavailable</span>
                ) : (
                  label && <span className={styles.configOptionPrice}>{label}</span>
                )}
              </button>
            );
          })}
          {!required && (
            <button
              type="button"
              className={`${styles.configOptionCard} ${
                selected === null ? styles.configOptionCardActive : ''
              }`}
              onClick={() => selectRequired(group, null)}
            >
              None
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderSubItemsStep = (step: Extract<Step, { kind: 'subitems' }>) => {
    const { group, impliedSize } = step;
    const effectiveSize = impliedSize ?? size;
    const quantities = subItemQuantities[group.id] ?? {};
    const totalTarget = getSizeTotalTarget(fetched as ItemDetails, group, effectiveSize);
    const currentTotal = sumGroupQuantities(quantities);

    return (
      <div className={styles.configStep}>
        <div className={styles.configStepHeaderRow}>
          <p className={styles.configStepLabel}>{group.label || group.name}</p>
          <span className={styles.configProgress}>
            {currentTotal}
            {totalTarget != null ? ` / ${totalTarget}` : ''}
          </span>
        </div>
        {totalTarget != null && currentTotal < totalTarget && (
          <p className={styles.cartItemError}>You are missing items in this category.</p>
        )}
        <div className={styles.configCounterList}>
          {group.items_as_modifier.map((option) => {
            const qty = quantities[option.id] ?? 0;
            const unavailable = isUnavailableAt(option as any, locationId);
            const canInc =
              !unavailable && canIncrementSubItem({ group, totalTarget, currentTotal });
            const label = deltaLabel(
              subItemDeltaPerIncrement(option, subItemGroupPricing(group, effectiveSize ?? 'FULL')),
            );
            return (
              <div key={option.id} className={styles.configCounterRow}>
                <span className={styles.configCounterName}>
                  {option.name}
                  {unavailable ? (
                    <span className={styles.configOptionalTag}> Unavailable</span>
                  ) : (
                    label && <span className={styles.configOptionPrice}> {label}</span>
                  )}
                </span>
                <div className={styles.configCounterControls}>
                  <button
                    type="button"
                    disabled={qty <= 0}
                    onClick={() => adjustSubItemQty(group, option.id, -(group.item_counter_increment || 1))}
                  >
                    −
                  </button>
                  <span className={styles.configCounterValue}>{qty}</span>
                  <button
                    type="button"
                    disabled={!canInc}
                    onClick={() => adjustSubItemQty(group, option.id, group.item_counter_increment || 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const adjustAddOn = (group: IBerryAddOnGroup, modId: number, delta: number, max: number) => {
    setAddOnQuantities((prev) => {
      const current = prev[group.id]?.[modId] ?? 0;
      const next = Math.min(Math.max(0, current + delta), max > 0 ? max : Number.MAX_SAFE_INTEGER);
      return { ...prev, [group.id]: { ...prev[group.id], [modId]: next } };
    });
  };

  const renderAddOnsStep = (step: Extract<Step, { kind: 'addons' }>) => {
    const { group } = step;
    return (
      <div className={styles.configStep}>
        <p className={styles.configStepLabel}>
          {group.label || group.name}
          <span className={styles.configOptionalTag}> (optional)</span>
        </p>
        <div className={styles.configCounterList}>
          {group.modifiers_groups_modifiers.map((m) => {
            const qty = addOnQuantities[group.id]?.[m.id] ?? 0;
            const unavailable = isUnavailableAt(m, locationId);
            const max = m.max ?? 0;
            const label = deltaLabel(m.price ?? 0);
            return (
              <div key={m.id} className={styles.configCounterRow}>
                <span className={styles.configCounterName}>
                  {m.name}
                  {unavailable ? (
                    <span className={styles.configOptionalTag}> Unavailable</span>
                  ) : (
                    label && <span className={styles.configOptionPrice}> {label} each</span>
                  )}
                </span>
                <div className={styles.configCounterControls}>
                  <button
                    type="button"
                    disabled={qty <= 0}
                    aria-label={`Fewer ${m.name}`}
                    onClick={() => adjustAddOn(group, m.id, -1, max)}
                  >
                    −
                  </button>
                  <span className={styles.configCounterValue}>{qty}</span>
                  <button
                    type="button"
                    disabled={unavailable || (max > 0 && qty >= max)}
                    aria-label={`More ${m.name}`}
                    onClick={() => adjustAddOn(group, m.id, 1, max)}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPackageCategoryStep = (step: Extract<Step, { kind: 'package_category' }>) => {
    const { category, manual } = step;
    if (!manual) {
      const included = category.items?.[0];
      return (
        <div className={styles.configStep}>
          <p className={styles.configStepLabel}>{category.name}</p>
          <div className={styles.configIncludedRow}>
            <span>{included?.name ?? category.name}</span>
            <span className={styles.configOptionalTag}>Included</span>
          </div>
        </div>
      );
    }
    const selected = packageChoices[category.id];
    return (
      <div className={styles.configStep}>
        <p className={styles.configStepLabel}>{category.name}</p>
        <div className={styles.configOptionList}>
          {category.items.map((candidate) => {
            const unavailable = isUnavailableAt(candidate as any, locationId);
            const label = deltaLabel(pickDelta(candidate as any));
            return (
              <button
                key={candidate.id}
                type="button"
                disabled={unavailable}
                className={`${styles.configOptionCard} ${
                  selected?.id === candidate.id ? styles.configOptionCardActive : ''
                }`}
                onClick={() =>
                  setPackageChoices((prev) => ({ ...prev, [category.id]: candidate }))
                }
              >
                <span>{candidate.name}</span>
                {unavailable ? (
                  <span className={styles.configOptionalTag}>Unavailable</span>
                ) : (
                  label && <span className={styles.configOptionPrice}>{label}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const selection = buildSelection();
    if (!selection) return null;
    return (
      <div className={styles.configStep}>
        <p className={styles.configStepLabel}>Review</p>
        <ul className={styles.configReviewList}>
          {selection.size && (
            <li>
              {family === 'box_lunch' ? '' : 'Size: '}
              {sizeOptionLabel(selection.size)}
            </li>
          )}
          {selection.requiredChoices.map((c) => (
            <li key={c.groupId}>{c.name}</li>
          ))}
          {selection.subItems
            .filter((s) => s.quantity > 0)
            .map((s) => (
              <li key={s.itemId}>
                {s.name} × {s.quantity}
              </li>
            ))}
          {selection.packageCategories.map((c) => (
            <React.Fragment key={c.categoryId}>
              <li>
                {c.categoryName}: {c.chosenItem?.name ?? '—'}
              </li>
              {(c.nestedRequiredChoices ?? []).map((n) => (
                <li key={`${c.categoryId}-r-${n.groupId}`}>{n.name}</li>
              ))}
              {(c.nestedSubItems ?? [])
                .filter((n) => n.quantity > 0)
                .map((n) => (
                  <li key={`${c.categoryId}-s-${n.itemId}`}>
                    {n.name} × {n.quantity}
                  </li>
                ))}
            </React.Fragment>
          ))}
          {selection.packageOptional
            .filter((o) => o.choice)
            .map((o) => (
              <li key={o.groupId}>{o.choice?.name}</li>
            ))}
          {(selection.addOns ?? []).map((a) => (
            <li key={`addon-${a.modifierId}`}>
              {a.name} × {a.quantity}
            </li>
          ))}
          {selection.siteChanges &&
            (selection.siteChanges.length > 0 ? (
              selection.siteChanges.map((line) => <li key={`site-${line}`}>{line}</li>)
            ) : (
              <li>As listed on the menu</li>
            ))}
        </ul>
        <div className={styles.configStepHeaderRow}>
          <span className={styles.configStepLabel}>Quantity</span>
          <div className={styles.qtyStepper}>
            <button
              type="button"
              className={styles.qtyBtn}
              disabled={quantity <= 1}
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className={styles.qtyValue}>{quantity}</span>
            <button
              type="button"
              className={styles.qtyBtn}
              onClick={() => setQuantity((q) => q + 1)}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>
        <p className={styles.configReviewTotal}>Total: {priceLabel(selectionLineTotal(selection))}</p>
      </div>
    );
  };

  const renderSiteCustomizeStep = () => {
    if (!siteCustomization) {
      return <p className={styles.configStepLabel}>Loading options…</p>;
    }
    return (
      <div className={styles.configStep}>
        <SiteItemCustomizer customization={siteCustomization} />
        {siteCustomization.missingRequired && (
          <p className={styles.cartItemError}>
            Please choose {siteCustomization.missingRequired} to continue.
          </p>
        )}
        <p className={styles.configReviewTotal}>
          Tray price: {priceLabel(siteCustomization.unitPrice)}
        </p>
      </div>
    );
  };

  const renderStep = () => {
    if (!currentStep) return null;
    switch (currentStep.kind) {
      case 'size':
        return renderSizeStep();
      case 'required':
        return renderRequiredStep(currentStep);
      case 'subitems':
        return renderSubItemsStep(currentStep);
      case 'package_category':
        return renderPackageCategoryStep(currentStep);
      case 'addons':
        return renderAddOnsStep(currentStep);
      case 'site_customize':
        return renderSiteCustomizeStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <CustomModal
      showModal={show}
      title={item.name}
      closeModal={onCancel}
      modalDialogClasses={`${styles.configModalDialog} ${
        usesSiteCustomization && currentStep?.kind === 'site_customize'
          ? styles.configModalDialogWide
          : ''
      }`}
      modalBodyClass={styles.configModalBody}
      size="sm"
    >
      {isLoading && <p className={styles.configStepLabel}>Loading options…</p>}
      {error && <p className={styles.cartItemError}>{error}</p>}
      {!isLoading && !error && fetched && (
        <>
          {renderSizePlanNote()}
          {renderStep()}
          {submitError && <p className={styles.cartItemError}>{submitError}</p>}
          <div className={styles.configFooter}>
            {stepIndex > 0 && (
              <button type="button" className={styles.configBackBtn} onClick={goBack}>
                Back
              </button>
            )}
            {!isLastStep ? (
              <button
                type="button"
                className={styles.configNextBtn}
                disabled={!canProceed}
                onClick={goNext}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                className={styles.configNextBtn}
                disabled={isSubmitting}
                onClick={handleConfirm}
              >
                {isSubmitting ? 'Adding…' : quantity > 1 ? `Add ${quantity} to Cart` : 'Add to Cart'}
              </button>
            )}
          </div>
        </>
      )}
    </CustomModal>
  );
};

export default ItemConfigPicker;
